import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { FaDownload, FaRegTrashAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  DatepickerV2,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
} from '@client/toolkit';

import {
  ErpSystem,
  FinancialReportUploadResult,
  PermissionKeys,
  useDeleteSpacesByIdFinancialReportsAndYearMutation,
  useGetSpacesByIdFinancialReportsQuery,
  useLazyGetSpacesByIdFinancialReportsAndYearFileQuery,
  usePostSpacesByIdFinancialReportsAndYearMutation,
} from '@client/api';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

export interface FinancialReportCardProps {
  buildingId?: string;
}

// Tripletex is the only supported export in v1; the rest are advertised but disabled until their
// parser column profiles are added on the backend.
const ERP_OPTIONS: { value: ErpSystem; enabled: boolean }[] = [
  { value: 'Tripletex', enabled: true },
  { value: 'Fiken', enabled: false },
  { value: 'PowerOffice', enabled: false },
  { value: 'Visma', enabled: false },
];

const ACCEPTED_FILE_TYPES = '.csv,.txt';

// 1–12; full-year exports use 12 and are not annualised in the valuation.
const MONTHS_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

// 0 = full year (quarter omitted); 1–4 map to the backend's quarter query parameter.
const FULL_YEAR = 0;
const QUARTER_OPTIONS = [1, 2, 3, 4];

export function FinancialReportCard({ buildingId }: FinancialReportCardProps) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('portfolio');

  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [erpSystem, setErpSystem] = useState<ErpSystem>('Tripletex');
  const [monthsCovered, setMonthsCovered] = useState(12);
  const [quarter, setQuarter] = useState<number>(FULL_YEAR);
  const [accumulated, setAccumulated] = useState(true);
  const [lastUpload, setLastUpload] = useState<FinancialReportUploadResult | null>(null);

  const isQuarterly = quarter !== FULL_YEAR;

  const { data: reports, isLoading } = useGetSpacesByIdFinancialReportsQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const [uploadReport, { isLoading: isUploading }] = usePostSpacesByIdFinancialReportsAndYearMutation();
  const [downloadReport] = useLazyGetSpacesByIdFinancialReportsAndYearFileQuery();
  const [deleteReport] = useDeleteSpacesByIdFinancialReportsAndYearMutation();
  const { hasAnyPermission } = useSpacePermissions({ id: buildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickUpload = useCallback(() => inputRef.current?.click(), []);

  const handleYearChange = (value: Dayjs | null) => {
    if (value) setSelectedYear(value);
  };

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (inputRef.current) inputRef.current.value = '';
      if (!buildingId || !file) return;

      try {
        // MonthsCovered is derived server-side for quarterly uploads (Q2 YTD = 6, Q2 isolated = 3).
        const result = await uploadReport({
          id: buildingId,
          year: selectedYear.year(),
          ...(isQuarterly ? { quarter, accumulated } : {}),
          body: { File: file, ErpSystem: erpSystem, MonthsCovered: monthsCovered },
        }).unwrap();
        setLastUpload(result);
        toast.success(t('economy.financialReport.uploadSuccess', { year: result.year }));
      } catch {
        toast.error(t('economy.financialReport.uploadFailed'));
      }
    },
    [buildingId, selectedYear, erpSystem, monthsCovered, isQuarterly, quarter, accumulated, uploadReport, t],
  );

  const handleDownload = useCallback(
    async (year: number, fileName: string, reportQuarter?: number | null) => {
      if (!buildingId) return;
      try {
        const { data } = await downloadReport({ id: buildingId, year, quarter: reportQuarter ?? undefined });
        if ((data as unknown) instanceof Blob) {
          const blobUrl = window.URL.createObjectURL(data as unknown as Blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }
      } catch {
        toast.error(t('economy.financialReport.downloadFailed'));
      }
    },
    [buildingId, downloadReport, t],
  );

  const handleDelete = useCallback(
    async (year: number, reportQuarter?: number | null) => {
      if (!buildingId) return;
      await deleteReport({ id: buildingId, year, quarter: reportQuarter ?? undefined });
      setLastUpload((current) =>
        current?.year === year && (current?.quarter ?? null) === (reportQuarter ?? null) ? null : current,
      );
    },
    [buildingId, deleteReport],
  );

  const formatUploadedAt = (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm');

  const formatPeriod = (reportQuarter?: number | null, isAccumulated?: boolean) =>
    reportQuarter == null
      ? t('economy.financialReport.periodFullYear')
      : t(
          isAccumulated
            ? 'economy.financialReport.periodQuarterAccumulated'
            : 'economy.financialReport.periodQuarterIsolated',
          { quarter: reportQuarter },
        );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="highlighted">
          {t('economy.financialReport.title')}
        </CardTitle>
        {canEdit && (
          <CardActions>
            <Select
              size="medium"
              value={erpSystem}
              onChange={(e) => setErpSystem(e.target.value as ErpSystem)}
              aria-label={t('economy.financialReport.erpSystem')}
            >
              {ERP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} disabled={!option.enabled}>
                  {t(`economy.financialReport.erp.${option.value}`)}
                  {option.enabled ? '' : ` (${t('economy.financialReport.comingSoon')})`}
                </option>
              ))}
            </Select>
            <DatepickerV2
              views={['year']}
              value={selectedYear}
              onChange={handleYearChange}
              format="YYYY"
              sx={{ flexShrink: 0 }}
            />
            <Select
              size="medium"
              value={quarter}
              onChange={(e) => setQuarter(Number(e.target.value))}
              aria-label={t('economy.financialReport.period')}
            >
              <option value={FULL_YEAR}>{t('economy.financialReport.periodFullYear')}</option>
              {QUARTER_OPTIONS.map((q) => (
                <option key={q} value={q}>
                  {t('economy.financialReport.periodQuarter', { quarter: q })}
                </option>
              ))}
            </Select>
            {isQuarterly ? (
              <Select
                size="medium"
                value={accumulated ? 'ytd' : 'isolated'}
                onChange={(e) => setAccumulated(e.target.value === 'ytd')}
                aria-label={t('economy.financialReport.accumulation')}
              >
                <option value="ytd">{t('economy.financialReport.accumulatedYtd')}</option>
                <option value="isolated">{t('economy.financialReport.accumulatedIsolated')}</option>
              </Select>
            ) : (
              <Select
                size="medium"
                value={monthsCovered}
                onChange={(e) => setMonthsCovered(Number(e.target.value))}
                aria-label={t('economy.financialReport.monthsCovered')}
              >
                {MONTHS_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m === 12
                      ? t('economy.financialReport.monthsCoveredFull')
                      : t('economy.financialReport.monthsCoveredOption', { count: m })}
                  </option>
                ))}
              </Select>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              color="primary"
              className="whitespace-nowrap"
              onClick={handleClickUpload}
              disabled={!buildingId || isUploading}
            >
              {isUploading ? t('saving') : t('economy.financialReport.upload')}
            </Button>
          </CardActions>
        )}
      </CardHeader>

      <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
        <p className="text-white/60 text-xs mb-3">{t('economy.financialReport.info')}</p>

        {lastUpload && lastUpload.unmappedAccounts.length > 0 ? (
          <p className="text-amber-300 text-xs mb-3">
            {t('economy.financialReport.unmappedAccounts', {
              count: lastUpload.unmappedAccounts.length,
              accounts: lastUpload.unmappedAccounts.join(', '),
            })}
          </p>
        ) : null}

        <Table className="w-full rounded-md" size="xs">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('economy.financialReport.columns.year')}</TableHeadCell>
              <TableHeadCell>{t('economy.financialReport.columns.period')}</TableHeadCell>
              <TableHeadCell>{t('economy.financialReport.columns.months')}</TableHeadCell>
              <TableHeadCell>{t('economy.financialReport.columns.erp')}</TableHeadCell>
              <TableHeadCell>{t('economy.financialReport.columns.file')}</TableHeadCell>
              <TableHeadCell>{t('economy.financialReport.columns.uploadedAt')}</TableHeadCell>
              <TableHeadCell align="right">{t('economy.financialReport.columns.actions')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from(Array(2).keys()).map((x) => (
                <TableRow key={x}>
                  <TableCell loading />
                  <TableCell loading />
                  <TableCell loading />
                  <TableCell loading />
                  <TableCell loading />
                  <TableCell loading />
                  <TableCell loading />
                </TableRow>
              ))
            ) : !reports || reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <span className="text-white/60">{t('economy.financialReport.empty')}</span>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.id} verticalAlign="middle">
                  <TableCell>{report.year}</TableCell>
                  <TableCell>{formatPeriod(report.quarter, report.isAccumulated)}</TableCell>
                  <TableCell>{report.monthsCovered}</TableCell>
                  <TableCell>{t(`economy.financialReport.erp.${report.erpSystem}`)}</TableCell>
                  <TableCell>{report.fileName}</TableCell>
                  <TableCell>{formatUploadedAt(report.uploadedAt)}</TableCell>
                  <TableCell align="right">
                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        onClick={() => handleDownload(report.year, report.fileName, report.quarter)}
                        className="text-white/70 hover:text-white"
                        title={tCommon('download')}
                      >
                        <FaDownload />
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleDelete(report.year, report.quarter)}
                          className="text-white/70 hover:text-rose-300"
                          title={tCommon('delete')}
                        >
                          <FaRegTrashAlt />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
